import { type ReactNode } from 'react';
import { tv } from 'tailwind-variants';

const root = tv({
  /* Spec Card→Card / hub band rhythm — same vertical stack as ListPageLayout. */
  base: 'flex flex-col gap-[var(--space-6)]',
});

export type HubDashboardKind = 'ops' | 'analytics';
/** Alias for Spec / SpecLayoutAliases consumers. */
export type HubDashboardLayoutKind = HubDashboardKind;

export interface HubDashboardLayoutProps {
  /** KPI / Statistics strip — typically `KpiGrid` of `KpiCard` cells. */
  readonly kpi?: ReactNode;
  /**
   * Page-scoped secondary actions (canonical Obnovit via `ScreenContentActions`).
   * Ops only — Analytics-kind omits / skips empty `contentActions`.
   */
  readonly contentActions?: ReactNode;
  /** Filters / FilterPanel / envelope — Analytics-kind renders before KPI. */
  readonly toolbar?: ReactNode;
  /** Dashboard body zones (panels, tables, widgets). */
  readonly zones?: ReactNode;
  readonly className?: string;
  /**
   * `ops`: kpi → contentActions → toolbar → zones
   * `analytics`: toolbar → kpi → zones (contentActions omitted)
   */
  readonly kind?: HubDashboardLayoutKind;
  readonly testId?: string;
}

/** Consumer testids for hub KPI empty / error / skeleton binaries (DB-02/03). */
export const HUB_DASHBOARD_KPI_EMPTY_TEST_ID = 'hub-dashboard-kpi-empty';
export const HUB_DASHBOARD_KPI_ERROR_TEST_ID = 'hub-dashboard-kpi-error';
export const HUB_DASHBOARD_KPI_SKELETON_TEST_ID = 'hub-dashboard-kpi-skeleton';

/**
 * Canonical vertical stack for admin hub dashboards under `EnterpriseShell` main.
 *
 * Ownership:
 * - Page inset + main track → `EnterpriseShell` only (no second page shell)
 * - Page H1 → `EnterprisePageHeader` only
 * - This stack → slot order by `kind` (ops vs Analytics-kind)
 * - SegmentNav / mode switch / health-band / scorecard tabs stay **outside** this layout
 *
 * Gold references: TCC / PCC (ops); AnalyticsScreenView / DevAnalyticsScreenView (analytics).
 */
export function HubDashboardLayout({
  kpi,
  contentActions,
  toolbar,
  zones,
  className,
  kind = 'ops',
  testId = 'hub-dashboard-layout',
}: HubDashboardLayoutProps): JSX.Element {
  const kpiSlot = kpi ? <div data-testid="hub-dashboard-kpi">{kpi}</div> : null;
  const contentActionsSlot =
    kind === 'ops' && contentActions ? (
      <div data-testid="hub-dashboard-content-actions">{contentActions}</div>
    ) : null;
  const toolbarSlot = toolbar ? (
    <div data-testid="hub-dashboard-toolbar">{toolbar}</div>
  ) : null;
  const zonesSlot = zones ? (
    <div className="flex min-h-0 flex-col gap-[var(--space-6)]" data-testid="hub-dashboard-zones">
      {zones}
    </div>
  ) : null;

  if (kind === 'analytics') {
    return (
      <div
        className={root({ className })}
        data-testid={testId}
        data-kind="analytics"
        data-hub-dashboard-layout="true"
      >
        {toolbarSlot}
        {kpiSlot}
        {zonesSlot}
      </div>
    );
  }

  return (
    <div
      className={root({ className })}
      data-testid={testId}
      data-kind="ops"
      data-hub-dashboard-layout="true"
    >
      {kpiSlot}
      {contentActionsSlot}
      {toolbarSlot}
      {zonesSlot}
    </div>
  );
}
